import { createContext, useContext } from "react";

import { Editor } from "@tiptap/core";
import { FilesPickerModalProps } from "components/modals/Files-picker/FilesPicker";
import { DocumentSectionType } from "constants/documents";
import { IRequestQueue } from "hooks";
import {
  Document,
  DocumentAnswer,
  DocumentQuestion,
  DocumentReportQueries,
  DocumentSection,
  DocumentSectionMeetingNotes,
  ICreateDocumentAIAnswerPayload,
  ICreateDocumentQuestion,
  IUpdateDocumentQuestionData,
  IUpdateDocumentSectionData,
  IUpdateMeetingSectionData,
  Optional,
  UpdateDocumentAnswerData
} from "store/types";

// AI request handed off to a freshly created answer section so that section runs it itself
// (documentId/sectionId are filled in by the receiving section).
export type ISectionAiRequest = Pick<
  ICreateDocumentAIAnswerPayload,
  "send_ai" | "query_type" | "user_prompt" | "file_ids" | "files"
>;

export interface ContentContextType {
  report_queries: DocumentReportQueries | null;

  // Files picker state
  filesPickerModalOpen: number | null;
  setFilesPickerModalOpen: (sectionId: number | null) => void;

  // Enter key state
  isEnterDisabled: boolean;
  setIsEnterDisabled: (isEnterDisabled: boolean) => void;

  // Section creation handlers
  createSection: <T extends DocumentSectionType = DocumentSectionType>(params: {
    position: number;
    type: T;
    text?: string;
    question?: string;
    files?: FileList;
    isAiMeeting?: boolean;
  }) => Promise<DocumentSection | undefined>;
  deleteSection: (data: { sectionId: number; isAiMeeting?: boolean }) => Promise<void>;
  updateSection: (data: IUpdateDocumentSectionData & { disableSetState?: boolean }) => Promise<DocumentSection>;
  updateMeetingSection: (data: {
    meetingId: number;
    body: IUpdateMeetingSectionData;
  }) => Promise<DocumentSectionMeetingNotes>;
  // handleFileUpload: (file: File, type: DocumentSectionType) => Promise<number>;

  // Questions
  createQuestion: (data: ICreateDocumentQuestion) => Promise<DocumentQuestion>;
  updateQuestion: (data: IUpdateDocumentQuestionData) => Promise<DocumentQuestion>;
  deleteQuestion: (questionId: number) => Promise<void>;

  // Answers
  createAnswer: (
    data: Optional<Pick<DocumentAnswer, "question_id" | "answer" | "parent_answer_id">, "parent_answer_id"> & {
      documentId: number;
    }
  ) => Promise<DocumentAnswer>;
  updateAnswer: (data: UpdateDocumentAnswerData & { disableSetState?: boolean }) => Promise<DocumentAnswer>;
  deleteAnswer: (answerId: number) => Promise<void>;

  reorderSections: (data: { order: { id: number; position: number }[]; documentId: number }) => void;
  askAIAnswer: (data: {
    question_id: number;
    documentId: number;
  }) => Promise<{ question: DocumentQuestion; answer: DocumentAnswer }>;
  AIAnswerSection: (data: ICreateDocumentAIAnswerPayload) => Promise<DocumentSection>;

  // Selection state
  selectionMode: boolean;
  setSelectionMode: (mode: boolean) => void;
  selectedSections: number[];
  handleSelectSection: (sectionId: number, selected: boolean) => void;

  // Share handlers
  handleShare: (sectionId: number) => void;
  handleMultiShare: () => void;

  // Drop handlers
  handleDrop: (files: FileList) => void;
  handleHTMLDrop: (html: string) => void;
  onMoveDocument: () => void;

  updateDocument: (data: Partial<Pick<Document, "title" | "is_private">> & Pick<Document, "id">) => void;
  updateDocumentReportQueries: (data: DocumentReportQueries) => void;

  // Paste handlers
  handlePaste: (event: ClipboardEvent) => void;

  // Focus handlers
  handleSectionFocus: (index: number) => void;
  handleContainerClick: (e: React.MouseEvent, containerRef: React.RefObject<HTMLDivElement>) => void;
  handleEmptySectionFocus: () => void;
  cubicsQueue: IRequestQueue;
  tablesQueue: IRequestQueue;
  cashflowGraphQueue: IRequestQueue;

  getAnswers: (data?: Partial<Pick<DocumentAnswer, "question_id" | "parent_answer_id">>) => DocumentAnswer[];

  isSectionMarkedForDrag: (sectionId: number) => boolean;

  // Files picker params
  filesPickerParams: FilesPickerModalProps | null;

  // Track sections that should open ChatInput
  sectionsToOpenChatInput: Set<number>;
  addSectionToOpenChatInput: (sectionId: number) => void;
  removeSectionFromOpenChatInput: (sectionId: number) => void;

  // Pending AI request handed to a freshly created answer section so it fires the request itself
  // (this keeps the "thinking" loader inside the new answer section instead of the source section)
  sectionsAiRequest: Map<number, ISectionAiRequest>;
  addSectionAiRequest: (sectionId: number, request: ISectionAiRequest) => void;
  removeSectionAiRequest: (sectionId: number) => void;

  setCurrentEditor: (editor: Editor | null) => void;
  currentSectionId: number | null;
  setCurrentSectionId: (sectionId: number | null) => void;
  sectionsNumber: number;

  // Text direction state
  currentSectionIsRtl: boolean | null;
  setCurrentSectionIsRtl: (isRtl: boolean) => void;
}

export const ContentContext = createContext<ContentContextType>({} as ContentContextType);

export const useContentContext = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error("useContentContext must be used within a ContentProvider");
  }
  return context;
};
